import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  Card,
  Row,
  Col,
  Table,
  Tag,
  Avatar,
  Space,
  Typography,
  Button,
  Input,
  Select,
  Tooltip,
  Badge,
  Empty,
  Modal,
  Tabs,
  notification,
  DatePicker,
  Divider,
  Statistic,
  List,
  Descriptions,
  Grid,
  Dropdown,
  Menu,
  Spin,
  Progress,
  Alert
} from 'antd';
import {
  SearchOutlined,
  DownloadOutlined,
  TrophyOutlined,
  TeamOutlined,
  BarChartOutlined,
  LineChartOutlined,
  CheckCircleOutlined,
  GlobalOutlined,
  PlusOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  FileOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  ClockCircleOutlined,
  RiseOutlined,
  FallOutlined,
  CarOutlined,
  PieChartOutlined,
  CustomerServiceOutlined,
  CalendarOutlined,
  DislikeOutlined,
  StarOutlined,
  SettingOutlined,
  ReloadOutlined,
  PrinterOutlined,
  StarFilled,
  CrownOutlined,
  AppstoreOutlined,
  TableOutlined,
  FileExcelOutlined,
  CloseOutlined,
  CheckOutlined,
  DashboardOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isBetween from 'dayjs/plugin/isBetween';
import updateLocale from 'dayjs/plugin/updateLocale';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  LineChart,
  Line,
  ComposedChart
} from 'recharts';
import { useReactToPrint } from 'react-to-print';
import API from '../../API/API';
import TabPane from 'antd/es/tabs/TabPane';
import { Legend } from 'chart.js';

// Initialize dayjs plugins
dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekOfYear);
dayjs.extend(isBetween);
dayjs.extend(updateLocale);

dayjs.updateLocale('en', { weekStart: 1 });

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

// ==================== PRODUCTION CONSTANTS ====================
const COLORS = {
  primary: '#1890ff',
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f',
  purple: '#722ed1',
  cyan: '#13c2c2',
  orange: '#fa8c16',
  geekblue: '#2f54eb',
  gold: '#fadb14',
  textPrimary: '#262626',
  textSecondary: '#8c8c8c',
  border: '#f0f0f0',
  background: '#f5f5f5',
  white: '#ffffff'
};

const STAGE_COLORS = {
  'New': '#1890ff',
  'Call Back': '#13c2c2',
  'Voice Mail': '#722ed1',
  'Follow-Up': '#fa8c16',
  'Quoted': '#faad14',
  'Pending Payment': '#fadb14',
  'Payment Completed': '#52c41a',
  'Sales Made': '#389e0d',
  'Part Not Available': '#8c8c8c',
  'Wrong Number': '#ff4d4f',
  'Not Interested': '#eb2f96',
  'Hang Up': '#ff7a45',
  'Fake': '#d4380d',
  'Test Lead': '#ffc53d',
  'Negotiation': '#7cb305',
  'Error': '#262626'
};

const TIER_COLORS = {
  'TIER-1': '#52c41a',
  'TIER-2': '#faad14',
  'TIER-3': '#ff4d4f'
};

// ==================== UTILS ====================
const formatNumber = (num) => new Intl.NumberFormat('en-US').format(num || 0);
const formatCompactNumber = (num) => {
  if (!num) return '0';
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
};

const getInitials = (name) => name?.charAt(0).toUpperCase() || 'U';

// ==================== PRODUCTION COMPONENTS ====================

// Production Metric Card
const ProductionMetricCard = ({ title, value, icon, color, trend, trendValue, suffix }) => (
  <Card 
    style={{ 
      borderRadius: '12px',
      border: `1px solid ${COLORS.border}`,
      background: COLORS.white
    }}
    bodyStyle={{ padding: '20px' }}
  >
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <Text type="secondary" style={{ fontSize: '13px', fontWeight: 500 }}>{title}</Text>
        <Avatar size={36} style={{ backgroundColor: `${color}15` }}>
          {React.cloneElement(icon, { style: { color, fontSize: '18px' } })}
        </Avatar>
      </Space>
      <div>
        <Text style={{ fontSize: '28px', fontWeight: 700, color: COLORS.textPrimary }}>
          {value}{suffix || ''}
        </Text>
        {trend && (
          <Tag color={trend === 'up' ? 'success' : 'error'} style={{ marginLeft: '12px' }}>
            {trend === 'up' ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {trendValue}%
          </Tag>
        )}
      </div>
    </Space>
  </Card>
);

// Production Status Badge
const ProductionStatusBadge = ({ status }) => {
  const config = {
    'Active': { color: 'success', text: 'Active' },
    'Inactive': { color: 'default', text: 'Inactive' }
  };
  const { color, text } = config[status] || { color: 'default', text: status };
  return <Tag color={color}>{text}</Tag>;
};

// Production Agent Avatar
const ProductionAgentAvatar = ({ name, isActive, size = 40, rank }) => (
  <div style={{ position: 'relative', display: 'inline-block' }}>
    <Avatar 
      size={size} 
      style={{ 
        backgroundColor: rank === 1 ? COLORS.gold : rank === 2 ? '#C0C0C0' : rank === 3 ? '#CD7F32' : COLORS.primary,
        fontWeight: 'bold'
      }}
    >
      {getInitials(name)}
    </Avatar>
    {isActive && (
      <div style={{
        position: 'absolute',
        bottom: 0,
        right: 0,
        width: size * 0.25,
        minWidth: 8,
        backgroundColor: COLORS.success,
        border: `2px solid ${COLORS.white}`,
        borderRadius: '50%',
        aspectRatio: '1/1'
      }} />
    )}
  </div>
);
const DateRangeSelector = ({ dateRange, onDateRangeChange }) => {
  const [isCustomOpen, setIsCustomOpen] = useState(false);
  
  const handleQuickSelect = (type) => {
    const today = dayjs();
    let newRange;
    
    switch(type) {
      case 'today':
        newRange = [today, today];
        break;
      case 'yesterday':
        newRange = [today.subtract(1, 'day'), today.subtract(1, 'day')];
        break;
      case 'lastWeek':
        newRange = [today.subtract(7, 'day'), today];
        break;
      case 'lastMonth':
        newRange = [today.subtract(30, 'day'), today];
        break;
      case 'currentMonth':
        newRange = [today.startOf('month'), today];
        break;
      case 'currentYear':
        newRange = [today.startOf('year'), today];
        break;
      default:
        return;
    }
    onDateRangeChange(newRange);
  };
  
  return (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
      <Button 
        size="small" 
        type={dateRange[0].isSame(dayjs(), 'day') && dateRange[1].isSame(dayjs(), 'day') ? 'primary' : 'default'}
        onClick={() => handleQuickSelect('today')}
      >
        Today
      </Button>
      <Button 
        size="small"
        type={dateRange[0].isSame(dayjs().subtract(1, 'day'), 'day') && dateRange[1].isSame(dayjs().subtract(1, 'day'), 'day') ? 'primary' : 'default'}
        onClick={() => handleQuickSelect('yesterday')}
      >
        Yesterday
      </Button>
      <Button 
        size="small"
        type={dateRange[0].isSame(dayjs().subtract(7, 'day'), 'day') && dateRange[1].isSame(dayjs(), 'day') ? 'primary' : 'default'}
        onClick={() => handleQuickSelect('lastWeek')}
      >
        Last 7 Days
      </Button>
      <Button 
        size="small"
        type={dateRange[0].isSame(dayjs().subtract(30, 'day'), 'day') && dateRange[1].isSame(dayjs(), 'day') ? 'primary' : 'default'}
        onClick={() => handleQuickSelect('lastMonth')}
      >
        Last 30 Days
      </Button>
      <Button 
        size="small"
        type={dateRange[0].isSame(dayjs().startOf('month'), 'day') && dateRange[1].isSame(dayjs(), 'day') ? 'primary' : 'default'}
        onClick={() => handleQuickSelect('currentMonth')}
      >
        This Month
      </Button>
      <Button 
        size="small"
        type={dateRange[0].isSame(dayjs().startOf('year'), 'day') && dateRange[1].isSame(dayjs(), 'day') ? 'primary' : 'default'}
        onClick={() => handleQuickSelect('currentYear')}
      >
        This Year
      </Button>
      <Divider type="vertical" />
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <CalendarOutlined style={{ color: COLORS.textSecondary }} />
        <RangePicker
          value={dateRange}
          onChange={onDateRangeChange}
          size="small"
          format="MM/DD/YYYY"
          placeholder={['Start Date', 'End Date']}
        />
      </div>
    </div>
  );
};

const ProductionFilterBar = ({ filters, onFilterChange, onSearch, onClear, stages, dateRange, onDateRangeChange, onRefresh, refreshing, onExport, exportLoading, onPrint }) => (
  <Card style={{ borderRadius: '12px', marginBottom: '24px' }} bodyStyle={{ padding: '16px' }}>
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {/* Date Range & Action Buttons Row */}
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <div style={{ 
            padding: '12px 16px', 
            background: COLORS.background, 
            borderRadius: 8,
            border: `1px solid ${COLORS.border}`
          }}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              {/* Date Range Section */}
              <div>
                <Text strong style={{ fontSize: '12px', color: COLORS.textSecondary }}>
                  <CalendarOutlined /> Date Range
                </Text>
                <div style={{ marginTop: 8 }}>
                  <DateRangeSelector 
                    dateRange={dateRange} 
                    onDateRangeChange={onDateRangeChange} 
                  />
                </div>
              </div>
              
              {/* Action Buttons Section */}
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '12px',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingTop: '8px'
              }}>
                <Space wrap size={12}>
                  <Button 
                    type="default" 
                    icon={<SettingOutlined />}
                    onClick={() => {
                      onFilterChange('search', '');
                      onFilterChange('stage', 'all');
                      onFilterChange('tier', 'all');
                      onFilterChange('status', 'all');
                    }}
                  >
                    Reset Filters
                  </Button>
                  <Button icon={<ReloadOutlined spin={refreshing} />} onClick={onRefresh}>
                    Refresh
                  </Button>
                  <Dropdown
                    overlay={
                      <Menu>
                        <Menu.Item key="excel" icon={<FileExcelOutlined />} onClick={onExport}>
                          Export to Excel
                        </Menu.Item>
                        <Menu.Item key="print" icon={<PrinterOutlined />} onClick={onPrint}>
                          Print Report
                        </Menu.Item>
                      </Menu>
                    }
                  >
                    <Button icon={<DownloadOutlined />} loading={exportLoading}>Export</Button>
                  </Dropdown>
                </Space>
                
                <Space>
                  <Button type="primary" onClick={onSearch}>Apply Filters</Button>
                </Space>
              </div>
            </Space>
          </div>
        </Col>
      </Row>
      
      {/* Filter Row */}
      <Row gutter={[16, 16]}>
        <Col xs={24} md={6}>
          <Input
            placeholder="Search by agent name or email..."
            prefix={<SearchOutlined style={{ color: COLORS.textSecondary }} />}
            value={filters.search}
            onChange={(e) => onFilterChange('search', e.target.value)}
            allowClear
            size="middle"
          />
        </Col>
        <Col xs={12} md={4}>
          <Select
            placeholder="Stage"
            value={filters.stage}
            onChange={(value) => onFilterChange('stage', value)}
            style={{ width: '100%' }}
            allowClear
            size="middle"
          >
            <Option value="all">All Stages</Option>
            {stages.map(s => <Option key={s} value={s}>{s}</Option>)}
          </Select>
        </Col>
        <Col xs={12} md={4}>
          <Select
            placeholder="Tier"
            value={filters.tier}
            onChange={(value) => onFilterChange('tier', value)}
            style={{ width: '100%' }}
            allowClear
            size="middle"
          >
            <Option value="all">All Tiers</Option>
            <Option value="TIER-1">TIER-1</Option>
            <Option value="TIER-2">TIER-2</Option>
            <Option value="TIER-3">TIER-3</Option>
          </Select>
        </Col>
        <Col xs={12} md={4}>
          <Select
            placeholder="Status"
            value={filters.status}
            onChange={(value) => onFilterChange('status', value)}
            style={{ width: '100%' }}
            allowClear
            size="middle"
          >
            <Option value="all">All Status</Option>
            <Option value="active">Active Only</Option>
            <Option value="inactive">Inactive Only</Option>
          </Select>
        </Col>
      </Row>
    </Space>
  </Card>
);

// ==================== MAIN PRODUCTION DASHBOARD ====================
const AgentPerformanceDashboard = () => {
  const screens = useBreakpoint();
  const printRef = useRef();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentDetailModal, setAgentDetailModal] = useState(false);
const [dateRange, setDateRange] = useState([dayjs(), dayjs()]);
  const [filters, setFilters] = useState({
    search: '',
    stage: 'all',
    tier: 'all',
    status: 'all'
  });
  const [viewMode, setViewMode] = useState('grid');
  const [exportLoading, setExportLoading] = useState(false);

  const isMobile = !screens.md;
// ==================== DATA FETCHING ====================
const fetchData = useCallback(async () => {
  setLoading(true);
  try {
    const params = {
      startDate: dateRange[0].format("YYYY-MM-DD"),
      endDate: dateRange[1].format("YYYY-MM-DD")
    };
    const res = await API.dashboardApi(params);
    if (res.success) {
      setData(res);
    }
  } catch (err) {
    console.error("Error:", err);
    notification.error({
      message: 'Error loading dashboard',
      description: err.message
    });
  } finally {
    setLoading(false);
    setRefreshing(false);
  }
}, [dateRange]);

// Single useEffect to handle initial load and date range changes
const isInitialMount = useRef(true);

useEffect(() => {
  if (isInitialMount.current) {
    isInitialMount.current = false;
    fetchData();
  } else {
    fetchData();
  }
}, [dateRange]);
  // Process filtered data
  const processedData = useMemo(() => {
    if (!data) return null;

    let filteredAgents = data.agentBreakdown || [];
    
    // Apply filters
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filteredAgents = filteredAgents.filter(agent => 
        agent.agentInfo?.name?.toLowerCase().includes(searchLower) ||
        agent.agentInfo?.email?.toLowerCase().includes(searchLower)
      );
    }
    
    if (filters.stage !== 'all') {
      filteredAgents = filteredAgents.filter(agent => 
        (agent.performance?.stageBreakdown?.[filters.stage] || 0) > 0
      );
    }
    
    if (filters.tier !== 'all') {
      filteredAgents = filteredAgents.filter(agent => 
        (agent.performance?.tierBreakdown?.[filters.tier] || 0) > 0
      );
    }
    
    if (filters.status !== 'all') {
      filteredAgents = filteredAgents.filter(agent => 
        filters.status === 'active' ? agent.agentInfo?.isActive : !agent.agentInfo?.isActive
      );
    }

    const overall = data.overall || {};
    
    // Agent comparison data - sorted by total leads (high to low)
    const agentComparisonData = filteredAgents
      .map(agent => ({
        id: agent.agentId,
        name: agent.agentInfo?.name || 'Unassigned',
        isActive: agent.agentInfo?.isActive,
        email: agent.agentInfo?.email,
        role: agent.agentInfo?.role,
        totalLeads: agent.performance?.totalLeads || 0,
        quotes: agent.performance?.stageBreakdown?.['Quoted'] || 0,
        sales: agent.performance?.stageBreakdown?.['Sales Made'] || 0,
        voicemails: agent.performance?.stageBreakdown?.['Voice Mail'] || 0,
        callbacks: agent.performance?.stageBreakdown?.['Call Back'] || 0,
        wrongNumbers: agent.performance?.stageBreakdown?.['Wrong Number'] || 0,
        followups: agent.performance?.stageBreakdown?.['Follow-Up'] || 0,
        notInterested: agent.performance?.stageBreakdown?.['Not Interested'] || 0,
        tier1: agent.performance?.tierBreakdown?.['TIER-1'] || 0,
        tier2: agent.performance?.tierBreakdown?.['TIER-2'] || 0,
        tier3: agent.performance?.tierBreakdown?.['TIER-3'] || 0,
        inbound: agent.performance?.leadTypeBreakdown?.inbound || 0,
        outbound: agent.performance?.leadTypeBreakdown?.outbound || 0,
        conversionRate: agent.performance?.totalLeads ? 
          ((agent.performance?.stageBreakdown?.['Sales Made'] || 0) / agent.performance?.totalLeads * 100).toFixed(1) : 0
      }))
      .sort((a, b) => b.totalLeads - a.totalLeads);

    // Stage breakdown for pie chart
    const stageBreakdown = Object.keys(STAGE_COLORS)
      .map(stage => ({
        stage,
        count: overall.stageBreakdown?.[stage] || 0,
        color: STAGE_COLORS[stage]
      }))
      .filter(s => s.count > 0)
      .sort((a, b) => b.count - a.count);

    // Domain breakdown
    const domainBreakdown = Object.keys(overall.domainBreakdown || {})
      .map(domain => ({
        domain,
        shortName: domain.replace('.com', '').replace('premiumused', 'PU').replace('advanceused', 'AU').substring(0, 15),
        totalLeads: overall.domainBreakdown?.[domain] || 0,
        voiceMails: overall.domainStageBreakdown?.[domain]?.['Voice Mail'] || 0,
        wrongNumbers: overall.domainStageBreakdown?.[domain]?.['Wrong Number'] || 0,
        quotes: overall.domainStageBreakdown?.[domain]?.['Quoted'] || 0,
        sales: overall.domainStageBreakdown?.[domain]?.['Sales Made'] || 0
      }))
      .sort((a, b) => b.totalLeads - a.totalLeads)
      .slice(0, 8);

    // Summary stats
    const summary = {
      totalLeads: overall.totalLeads || 0,
      totalSales: overall.stageBreakdown?.['Sales Made'] || 0,
      totalQuotes: overall.stageBreakdown?.['Quoted'] || 0,
      totalVoicemails: overall.stageBreakdown?.['Voice Mail'] || 0,
      totalCallbacks: overall.stageBreakdown?.['Call Back'] || 0,
      totalWrongNumbers: overall.stageBreakdown?.['Wrong Number'] || 0,
      totalNotInterested: overall.stageBreakdown?.['Not Interested'] || 0,
      conversionRate: overall.totalLeads ? ((overall.stageBreakdown?.['Sales Made'] || 0) / overall.totalLeads * 100).toFixed(1) : 0,
      activeAgents: filteredAgents.filter(a => a.agentInfo?.isActive).length,
      totalAgents: filteredAgents.length,
      inboundLeads: overall.leadTypeBreakdown?.inbound || 0,
      outboundLeads: overall.leadTypeBreakdown?.outbound || 0,
      tier1Leads: overall.tierBreakdown?.['TIER-1'] || 0,
      tier2Leads: overall.tierBreakdown?.['TIER-2'] || 0,
      tier3Leads: overall.tierBreakdown?.['TIER-3'] || 0
    };

    return {
      agentComparisonData,
      stageBreakdown,
      domainBreakdown,
      summary,
      overall
    };
  }, [data, filters]);

  // Get unique filter options
  const uniqueStages = useMemo(() => {
    if (!data?.overall?.stageBreakdown) return [];
    return Object.keys(data.overall.stageBreakdown).filter(s => data.overall.stageBreakdown[s] > 0);
  }, [data]);

 const handleFilterChange = (key, value) => {
  setFilters(prev => ({ ...prev, [key]: value }));
};



const handleClearFilters = () => {
  setFilters({
    search: '',
    stage: 'all',
    tier: 'all',
    status: 'all'
  });
};

const handleRefresh = () => {
  setRefreshing(true);
  fetchData();
};

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const exportData = processedData?.agentComparisonData.map((agent, index) => ({
        'Rank': index + 1,
        'Agent Name': agent.name,
        'Status': agent.isActive ? 'Active' : 'Inactive',
        'Role': agent.role,
        'Email': agent.email,
        'Total Leads': agent.totalLeads,
        'Quotes': agent.quotes,
        'Sales': agent.sales,
        'Conversion Rate': `${agent.conversionRate}%`,
        'Voice Mails': agent.voicemails,
        'Call Backs': agent.callbacks,
        'Wrong Numbers': agent.wrongNumbers,
        'Follow-ups': agent.followups,
        'Not Interested': agent.notInterested,
        'TIER-1': agent.tier1,
        'TIER-2': agent.tier2,
        'TIER-3': agent.tier3,
        'Inbound': agent.inbound,
        'Outbound': agent.outbound
      }));
      
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Agent Performance Report');
      XLSX.writeFile(wb, `agent_performance_${dayjs().format('YYYY-MM-DD')}.xlsx`);
      
      notification.success({ message: 'Export Successful', description: 'Report exported to Excel' });
    } catch (error) {
      notification.error({ message: 'Export Failed', description: error.message });
    } finally {
      setExportLoading(false);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Agent_Performance_Report_${dayjs().format('YYYY-MM-DD')}`
  });

const handleDateRangeChange = (dates) => {
  setDateRange(dates);
};

useEffect(() => {
  if (dateRange[0] && dateRange[1]) {
    fetchData();
  }
}, [dateRange]); 
 const handleSearch = () => {
  fetchData();
};
  // ==================== RENDER FUNCTIONS ====================
  
// Update renderHeader to show selected date range
const renderHeader = () => (
  <div style={{ 
    background: COLORS.white, 
    padding: isMobile ? '16px' : '20px 24px',
    borderBottom: `1px solid ${COLORS.border}`,
    marginBottom: '24px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)'
  }}>
    <Row gutter={[16, 16]} align="middle">
      <Col flex="auto">
        <Space size={16}>
          <div style={{
            width: 44,
            height: 44,
            background: COLORS.primary,
            borderRadius: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <TeamOutlined style={{ color: COLORS.white, fontSize: 22 }} />
          </div>
          <div>
            <Title level={4} style={{ margin: 0 }}>Production Dashboard</Title>
            <Space split={<Divider type="vertical" />}>
              <Text type="secondary" style={{ fontSize: '13px' }}>
                <TeamOutlined /> {processedData?.summary.totalAgents || 0} Active Agents
              </Text>
              <Text type="secondary" style={{ fontSize: '13px' }}>
                <CalendarOutlined /> {dateRange[0].format('MMM DD, YYYY')} - {dateRange[1].format('MMM DD, YYYY')}
              </Text>
            </Space>
          </div>
        </Space>
      </Col>
    </Row>
  </div>
);
  const renderProductionStats = () => {
    const stats = processedData?.summary;
    if (!stats) return null;

    return (
      <Row gutter={[16, 16]} style={{ marginBottom: '24px', padding: isMobile ? '0 16px' : '0' }}>
        <Col xs={12} sm={12} md={6} lg={4}>
          <ProductionMetricCard
            title="Total Leads"
            value={formatCompactNumber(stats.totalLeads)}
            icon={<BarChartOutlined />}
            color={COLORS.primary}
          />
        </Col>
        <Col xs={12} sm={12} md={6} lg={4}>
          <ProductionMetricCard
            title="Sales"
            value={formatCompactNumber(stats.totalSales)}
            icon={<TrophyOutlined />}
            color={COLORS.success}
          />
        </Col>
        <Col xs={12} sm={12} md={6} lg={4}>
          <ProductionMetricCard
            title="Quotes"
            value={formatCompactNumber(stats.totalQuotes)}
            icon={<FileOutlined />}
            color={COLORS.warning}
          />
        </Col>
        <Col xs={12} sm={12} md={6} lg={4}>
          <ProductionMetricCard
            title="Conversion Rate"
            value={stats.conversionRate}
            icon={<LineChartOutlined />}
            color={COLORS.cyan}
            suffix="%"
          />
        </Col>
        <Col xs={12} sm={12} md={6} lg={4}>
          <ProductionMetricCard
            title="Voice Mails"
            value={formatCompactNumber(stats.totalVoicemails)}
            icon={<CustomerServiceOutlined />}
            color={COLORS.purple}
          />
        </Col>
        <Col xs={12} sm={12} md={6} lg={4}>
          <ProductionMetricCard
            title="Wrong Numbers"
            value={formatCompactNumber(stats.totalWrongNumbers)}
            icon={<DislikeOutlined />}
            color={COLORS.error}
          />
        </Col>
      </Row>
    );
  };

  // Updated Leaderboard - shows ALL agents in order
  const renderProductionLeaderboard = () => {
    const agents = processedData?.agentComparisonData || [];
    
    if (agents.length === 0) {
      return (
        <Card title="📊 Agent Performance Leaderboard" style={{ borderRadius: '12px', marginBottom: '24px' }}>
          <Empty description="No agents found" />
        </Card>
      );
    }

    return (
      <Card 
        title={
          <Space>
            <TrophyOutlined style={{ color: COLORS.gold }} />
            <span>Agent Performance Leaderboard</span>
            <Badge count={agents.length} style={{ backgroundColor: COLORS.primary }} />
          </Space>
        }
        style={{ borderRadius: '12px', marginBottom: '24px' }}
      >
        <List
          dataSource={agents}
          renderItem={(agent, index) => (
            <List.Item 
              style={{ 
                cursor: 'pointer',
                padding: '16px',
                transition: 'all 0.2s',
                borderRadius: '8px',
                marginBottom: '8px',
                background: index < 3 ? `${COLORS.gold}08` : 'transparent'
              }}
              onClick={() => {
                const fullAgent = data?.agentBreakdown?.find(a => a.agentId === agent.id);
                setSelectedAgent(fullAgent);
                setAgentDetailModal(true);
              }}
            >
              <Row style={{ width: '100%' }} align="middle" gutter={[16, 12]}>
                <Col xs={24} sm={4} md={3}>
                  <Space size={12}>
                    <div style={{ width: 40, textAlign: 'center' }}>
                      {index === 0 ? (
                        <CrownOutlined style={{ color: COLORS.gold, fontSize: 24 }} />
                      ) : index === 1 ? (
                        <TrophyOutlined style={{ color: '#C0C0C0', fontSize: 22 }} />
                      ) : index === 2 ? (
                        <TrophyOutlined style={{ color: '#CD7F32', fontSize: 22 }} />
                      ) : (
                        <Text strong style={{ fontSize: 16, color: COLORS.textSecondary }}>#{index + 1}</Text>
                      )}
                    </div>
                    <ProductionAgentAvatar 
                      name={agent.name} 
                      isActive={agent.isActive} 
                      size={44}
                      rank={index + 1}
                    />
                  </Space>
                </Col>
                <Col xs={12} sm={5} md={4}>
                  <div>
                    <Text strong style={{ fontSize: 15 }}>{agent.name}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>{agent.role || 'Agent'}</Text>
                  </div>
                </Col>
                <Col xs={6} sm={3} md={2}>
                  <div style={{ textAlign: 'center' }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>Leads</Text>
                    <div style={{ fontSize: 18, fontWeight: 600 }}>{agent.totalLeads}</div>
                  </div>
                </Col>
                <Col xs={6} sm={3} md={2}>
                  <div style={{ textAlign: 'center' }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>Sales</Text>
                    <div style={{ fontSize: 18, fontWeight: 600, color: COLORS.success }}>{agent.sales}</div>
                  </div>
                </Col>
                <Col xs={6} sm={3} md={2}>
                  <div style={{ textAlign: 'center' }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>Rate</Text>
                    <div style={{ fontSize: 16, fontWeight: 500, color: COLORS.primary }}>{agent.conversionRate}%</div>
                  </div>
                </Col>
                <Col xs={6} sm={3} md={2}>
                  <div style={{ textAlign: 'center' }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>Quotes</Text>
                    <div style={{ fontSize: 16, fontWeight: 500 }}>{agent.quotes}</div>
                  </div>
                </Col>
                <Col xs={12} sm={3} md={2}>
                  <div style={{ textAlign: 'center' }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>Tiers</Text>
                    <Space size={4}>
                      {agent.tier1 > 0 && <Tag color="success" style={{ fontSize: 11 }}>T1:{agent.tier1}</Tag>}
                      {agent.tier2 > 0 && <Tag color="warning" style={{ fontSize: 11 }}>T2:{agent.tier2}</Tag>}
                    </Space>
                  </div>
                </Col>
                <Col xs={12} sm={3} md={2}>
                  <ProductionStatusBadge status={agent.isActive ? 'Active' : 'Inactive'} />
                </Col>
              </Row>
            </List.Item>
          )}
        />
      </Card>
    );
  };

  const renderAgentsGridView = () => {
    const agents = processedData?.agentComparisonData || [];
    
    if (agents.length === 0) {
      return (
        <Empty 
          description="No agents match your filters"
          style={{ padding: '48px' }}
        />
      );
    }

    return (
      <Row gutter={[16, 16]}>
        {agents.map((agent, index) => (
          <Col xs={24} sm={12} md={8} lg={6} key={agent.id}>
            <Card
              hoverable
              style={{ 
                borderRadius: '12px', 
                height: '100%',
                position: 'relative',
                overflow: 'hidden'
              }}
              onClick={() => {
                const fullAgent = data?.agentBreakdown?.find(a => a.agentId === agent.id);
                setSelectedAgent(fullAgent);
                setAgentDetailModal(true);
              }}
            >
              {index === 0 && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: 60,
                  height: 60,
                  background: `linear-gradient(135deg, transparent 50%, ${COLORS.gold} 50%)`,
                  borderTopRightRadius: 12
                }}>
                  <CrownOutlined style={{ position: 'absolute', top: 8, right: 8, color: COLORS.white, fontSize: 16 }} />
                </div>
              )}
              
              <Space direction="vertical" size={14} style={{ width: '100%' }}>
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <ProductionAgentAvatar name={agent.name} isActive={agent.isActive} size={52} rank={index + 1} />
                  <ProductionStatusBadge status={agent.isActive ? 'Active' : 'Inactive'} />
                </Space>
                
                <div>
                  <Text strong style={{ fontSize: 16 }}>{agent.name}</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>{agent.role || 'Agent'}</Text>
                </div>
                
                <Row gutter={[8, 8]}>
                  <Col span={8}>
                    <div style={{ textAlign: 'center', padding: '8px', background: `${COLORS.primary}08`, borderRadius: 8 }}>
                      <Text type="secondary" style={{ fontSize: 11 }}>Leads</Text>
                      <div style={{ fontSize: 20, fontWeight: 700 }}>{formatNumber(agent.totalLeads)}</div>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ textAlign: 'center', padding: '8px', background: `${COLORS.success}08`, borderRadius: 8 }}>
                      <Text type="secondary" style={{ fontSize: 11 }}>Sales</Text>
                      <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.success }}>{agent.sales}</div>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ textAlign: 'center', padding: '8px', background: `${COLORS.warning}08`, borderRadius: 8 }}>
                      <Text type="secondary" style={{ fontSize: 11 }}>Rate</Text>
                      <div style={{ fontSize: 18, fontWeight: 600, color: COLORS.warning }}>{agent.conversionRate}%</div>
                    </div>
                  </Col>
                </Row>
                
                <Divider style={{ margin: '4px 0' }} />
                
                <Space wrap size={4}>
                  <Tag icon={<RiseOutlined />}>{agent.inbound} Inbound</Tag>
                  <Tag icon={<FallOutlined />}>{agent.outbound} Outbound</Tag>
                  {agent.tier1 > 0 && <Tag color="success">T1: {agent.tier1}</Tag>}
                  {agent.tier2 > 0 && <Tag color="warning">T2: {agent.tier2}</Tag>}
                  {agent.tier3 > 0 && <Tag color="error">T3: {agent.tier3}</Tag>}
                </Space>
                
                <Progress 
                  percent={Math.min(100, (agent.sales / Math.max(agent.totalLeads, 1)) * 100)} 
                  size="small"
                  strokeColor={COLORS.success}
                  showInfo={false}
                />
              </Space>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  const renderAgentsTableView = () => {
    const agents = processedData?.agentComparisonData || [];
    
    const columns = [
      {
        title: 'Rank',
        key: 'rank',
        width: 70,
        align: 'center',
        render: (_, __, index) => (
          index === 0 ? <CrownOutlined style={{ color: COLORS.gold, fontSize: 18 }} /> :
          index === 1 ? <TrophyOutlined style={{ color: '#C0C0C0', fontSize: 16 }} /> :
          index === 2 ? <TrophyOutlined style={{ color: '#CD7F32', fontSize: 16 }} /> :
          <Text strong>#{index + 1}</Text>
        )
      },
      {
        title: 'Sales-Agent',
        dataIndex: 'name',
        key: 'name',
        fixed: 'left',
        width: 200,
        render: (name, record) => (
          <Space>
            <ProductionAgentAvatar name={name} isActive={record.isActive} size={36} />
            <div>
              <div><Text strong>{name}</Text></div>
              <Text type="secondary" style={{ fontSize: 12 }}>{record.role}</Text>
            </div>
          </Space>
        )
      },
      {
        title: 'Total Leads',
        dataIndex: 'totalLeads',
        key: 'totalLeads',
        width: 110,
        align: 'right',
        sorter: (a, b) => a.totalLeads - b.totalLeads,
        render: (val) => <Text strong>{formatNumber(val)}</Text>
      },
      {
        title: 'Sales',
        dataIndex: 'sales',
        key: 'sales',
        width: 80,
        align: 'right',
        render: (val) => <Text style={{ color: COLORS.success, fontWeight: 600 }}>{val}</Text>
      },
      {
        title: 'Conv. Rate',
        dataIndex: 'conversionRate',
        key: 'conversionRate',
        width: 90,
        align: 'right',
        render: (val) => <Tag color="cyan">{val}%</Tag>
      },
      {
        title: 'Quotes',
        dataIndex: 'quotes',
        key: 'quotes',
        width: 80,
        align: 'right',
        render: (val) => <Text style={{ color: COLORS.warning }}>{val}</Text>
      },
      {
        title: 'Voice Mails',
        dataIndex: 'voicemails',
        key: 'voicemails',
        width: 100,
        align: 'right',
        render: (val) => <Text style={{ color: COLORS.purple }}>{val}</Text>
      },
      {
        title: 'Wrong #',
        dataIndex: 'wrongNumbers',
        key: 'wrongNumbers',
        width: 90,
        align: 'right',
        render: (val) => <Text style={{ color: COLORS.error }}>{val}</Text>
      },
      {
        title: 'Tier-1',
        dataIndex: 'tier1',
        key: 'tier1',
        width: 70,
        align: 'right',
        render: (val) => <Tag color="success">{val}</Tag>
      },
      {
        title: 'Tier-2',
        dataIndex: 'tier2',
        key: 'tier2',
        width: 70,
        align: 'right',
        render: (val) => <Tag color="warning">{val}</Tag>
      },
      {
        title: 'Status',
        dataIndex: 'isActive',
        key: 'status',
        width: 90,
        render: (isActive) => <ProductionStatusBadge status={isActive ? 'Active' : 'Inactive'} />
      }
    ];

    return (
      <Table
        dataSource={agents}
        columns={columns}
        rowKey="id"
        pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `${total} agents` }}
        scroll={{ x: 1200 }}
        size="middle"
      />
    );
  };

const renderStageDistribution = () => {
  const stages = processedData?.stageBreakdown || [];
  if (stages.length === 0) return null;

  const totalLeads = stages.reduce((sum, s) => sum + s.count, 0);
  
  // Sort stages by count descending
  const chartData = [...stages].sort((a, b) => b.count - a.count).slice(0, 10);

  return (
    <Card 
      title={
        <Space>
          <BarChartOutlined />
          <span>Lead Stages Distribution</span>
          <Badge count={`${formatNumber(totalLeads)} Total Leads`} style={{ backgroundColor: COLORS.primary }} />
        </Space>
      }
      style={{ borderRadius: '12px', marginBottom: '24px' }}
    >
      <div style={{ height: 400 }}>
        <ResponsiveContainer>
          <BarChart 
            data={chartData} 
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
            <XAxis 
              dataKey="stage" 
              angle={-45} 
              textAnchor="end" 
              height={80}
              tick={{ fontSize: 11 }}
            />
            <YAxis 
              tick={{ fontSize: 11 }}
              label={{ value: 'Number of Leads', angle: -90, position: 'insideLeft', offset: 0 }}
            />
            <RechartsTooltip 
              formatter={(value) => [`${formatNumber(value)} leads (${((value / totalLeads) * 100).toFixed(1)}%)`, 'Count']}
            />
            <Bar 
              dataKey="count" 
              name="Leads" 
              radius={[8, 8, 0, 0]}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      {/* Summary tags at the bottom */}
      <div style={{ marginTop: 24 }}>
        <Row gutter={[8, 8]}>
          {chartData.map((stage) => (
            <Col xs={12} sm={8} md={6} lg={4} key={stage.stage}>
              <div style={{ 
                padding: '8px 12px', 
                background: `${stage.color}10`, 
                borderRadius: 8,
                borderLeft: `3px solid ${stage.color}`
              }}>
                <Text style={{ fontSize: 12, color: COLORS.textSecondary }}>{stage.stage}</Text>
                <div>
                  <Text style={{ fontSize: 16, fontWeight: 600, color: stage.color }}>
                    {formatNumber(stage.count)}
                  </Text>
                  <Text style={{ fontSize: 11, marginLeft: 4, color: COLORS.textSecondary }}>
                    ({((stage.count / totalLeads) * 100).toFixed(1)}%)
                  </Text>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </div>
    </Card>
  );
};

const renderDomainPerformance = () => {
  const domains = processedData?.domainBreakdown || [];
  if (domains.length === 0) return null;

  const totalLeads = domains.reduce((sum, d) => sum + d.totalLeads, 0);
  const totalSales = domains.reduce((sum, d) => sum + (d.sales || 0), 0);

  return (
    <Card 
      title={
        <Space>
          <GlobalOutlined />
          <span>Domain Performance</span>
          <Badge count={`${domains.length} Domains`} style={{ backgroundColor: COLORS.geekblue }} />
        </Space>
      }
      style={{ borderRadius: '12px', marginBottom: '24px' }}
    >
      <div style={{ height: 360 }}>
        <ResponsiveContainer>
          <BarChart 
            data={domains} 
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
            <XAxis 
              dataKey="shortName" 
              angle={-30} 
              textAnchor="end" 
              height={70}
              tick={{ fontSize: 11 }}
            />
            <YAxis 
              tick={{ fontSize: 11 }}
              label={{ value: 'Count', angle: -90, position: 'insideLeft', offset: 0 }}
            />
            <RechartsTooltip 
              contentStyle={{ borderRadius: 8, border: `1px solid ${COLORS.border}` }}
            />
            <Bar 
              dataKey="totalLeads" 
              name="Total Leads" 
              fill={COLORS.primary} 
              radius={[4, 4, 0, 0]} 
              barSize={40}
            />
            <Bar 
              dataKey="quotes" 
              name="Quotes" 
              fill={COLORS.warning} 
              radius={[4, 4, 0, 0]} 
              barSize={40}
            />
            <Bar 
              dataKey="sales" 
              name="Sales" 
              fill={COLORS.success} 
              radius={[4, 4, 0, 0]} 
              barSize={40}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      {/* Domain Summary Table - Shows ALL domains */}
      <div style={{ marginTop: 24 }}>
        <Table
          dataSource={domains}
          rowKey="domain"
          size="small"
          pagination={{ 
            pageSize: 10, 
            showSizeChanger: true, 
            showTotal: (total) => `${total} domains`,
            pageSizeOptions: ['10', '20', '50']
          }}
          columns={[
            {
              title: 'Domain',
              dataIndex: 'shortName',
              key: 'domain',
              width: 200,
              render: (text, record) => (
                <Tooltip title={record.domain}>
                  <Space>
                    <div style={{ 
                      width: 8, 
                      height: 8, 
                      backgroundColor: COLORS.geekblue, 
                      borderRadius: 2 
                    }} />
                    <Text strong>{text}</Text>
                  </Space>
                </Tooltip>
              )
            },
            {
              title: 'Total Leads',
              dataIndex: 'totalLeads',
              key: 'totalLeads',
              width: 120,
              align: 'right',
              sorter: (a, b) => a.totalLeads - b.totalLeads,
              render: (val) => <Text style={{ color: COLORS.primary, fontWeight: 600 }}>{formatNumber(val)}</Text>
            },
            {
              title: 'Quotes',
              dataIndex: 'quotes',
              key: 'quotes',
              width: 100,
              align: 'right',
              render: (val) => <Text style={{ color: COLORS.warning }}>{formatNumber(val || 0)}</Text>
            },
            {
              title: 'Sales',
              dataIndex: 'sales',
              key: 'sales',
              width: 100,
              align: 'right',
              render: (val) => <Text style={{ color: COLORS.success, fontWeight: 600 }}>{formatNumber(val || 0)}</Text>
            },
            {
              title: 'Conv. Rate',
              key: 'conversionRate',
              width: 120,
              align: 'right',
              render: (_, record) => {
                const rate = record.totalLeads ? ((record.sales || 0) / record.totalLeads * 100).toFixed(1) : 0;
                return (
                  <Space>
                    <Progress 
                      percent={parseFloat(rate)} 
                      size="small" 
                      style={{ width: 80 }}
                      strokeColor={rate > 20 ? COLORS.success : rate > 10 ? COLORS.warning : COLORS.error}
                      showInfo={false}
                    />
                    <Text style={{ color: rate > 20 ? COLORS.success : rate > 10 ? COLORS.warning : COLORS.error }}>
                      {rate}%
                    </Text>
                  </Space>
                );
              }
            },
            {
              title: 'Voice Mails',
              dataIndex: 'voiceMails',
              key: 'voiceMails',
              width: 110,
              align: 'right',
              render: (val) => <Text style={{ color: COLORS.purple }}>{formatNumber(val || 0)}</Text>
            },
            {
              title: 'Wrong #',
              dataIndex: 'wrongNumbers',
              key: 'wrongNumbers',
              width: 100,
              align: 'right',
              render: (val) => <Text style={{ color: COLORS.error }}>{formatNumber(val || 0)}</Text>
            }
          ]}
        />
        
        {/* Summary Stats */}
        <div style={{ marginTop: 16, padding: 12, background: COLORS.background, borderRadius: 8 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Text type="secondary">Total Leads Across All Domains</Text>
              <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.primary }}>{formatNumber(totalLeads)}</div>
            </Col>
            <Col span={8}>
              <Text type="secondary">Total Sales Across All Domains</Text>
              <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.success }}>{formatNumber(totalSales)}</div>
            </Col>
            <Col span={8}>
              <Text type="secondary">Overall Conversion Rate</Text>
              <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.warning }}>
                {totalLeads ? ((totalSales / totalLeads) * 100).toFixed(1) : 0}%
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </Card>
  );
};

const renderPerformanceChart = () => {
  const agents = processedData?.agentComparisonData || [];
  const chartData = agents.slice(0, 10).map(agent => ({
    name: agent.name.length > 12 ? agent.name.substring(0, 10) + '...' : agent.name,
    leads: agent.totalLeads,
    sales: agent.sales,
    conversionRate: parseFloat(agent.conversionRate)
  }));

  if (chartData.length === 0) return null;

  return (
    <Card 
      title={
        <Space>
          <LineChartOutlined />
          <span>Agent Performance Analysis</span>
          <Badge count="Top 10 Agents" style={{ backgroundColor: COLORS.cyan }} />
        </Space>
      }
      style={{ borderRadius: '12px', marginBottom: '24px' }}
    >
      <div style={{ height: 400 }}>
        <ResponsiveContainer>
          <ComposedChart 
            data={chartData} 
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
            <XAxis 
              dataKey="name" 
              angle={-35} 
              textAnchor="end" 
              height={70}
              tick={{ fontSize: 11, fontWeight: 500 }}
            />
            <YAxis 
              yAxisId="left" 
              tick={{ fontSize: 11 }}
              label={{ value: 'Leads / Sales', angle: -90, position: 'insideLeft', offset: 0 }}
            />
            <YAxis 
              yAxisId="right" 
              orientation="right" 
              tick={{ fontSize: 11 }}
              label={{ value: 'Conversion Rate (%)', angle: 90, position: 'insideRight', offset: 0 }}
            />
            <RechartsTooltip 
              contentStyle={{ borderRadius: 8, border: `1px solid ${COLORS.border}` }}
            />
            <Legend verticalAlign="top" height={36} />
            <Bar 
              yAxisId="left" 
              dataKey="leads" 
              name="Total Leads" 
              fill={COLORS.primary} 
              radius={[4, 4, 0, 0]} 
              barSize={35}
            />
            <Bar 
              yAxisId="left" 
              dataKey="sales" 
              name="Sales" 
              fill={COLORS.success} 
              radius={[4, 4, 0, 0]} 
              barSize={35}
            />
            <Line 
              yAxisId="right" 
              type="monotone" 
              dataKey="conversionRate" 
              name="Conversion Rate %" 
              stroke={COLORS.warning} 
              strokeWidth={3}
              dot={{ r: 5, fill: COLORS.warning, strokeWidth: 2 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      
      {/* Performance Summary Stats */}
      <div style={{ marginTop: 24, paddingTop: 16, borderTop: `1px solid ${COLORS.border}` }}>
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={6}>
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Avg Leads/Agent</Text>
              <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.primary }}>
                {formatNumber(Math.round(chartData.reduce((sum, a) => sum + a.leads, 0) / chartData.length))}
              </div>
            </div>
          </Col>
          <Col xs={12} sm={6}>
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Avg Sales/Agent</Text>
              <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.success }}>
                {formatNumber(Math.round(chartData.reduce((sum, a) => sum + a.sales, 0) / chartData.length))}
              </div>
            </div>
          </Col>
          <Col xs={12} sm={6}>
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Top Conversion Rate</Text>
              <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.warning }}>
                {Math.max(...chartData.map(a => a.conversionRate), 0)}%
              </div>
            </div>
          </Col>
          <Col xs={12} sm={6}>
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Total Performance</Text>
              <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.geekblue }}>
                {formatNumber(chartData.reduce((sum, a) => sum + a.leads, 0))} Leads
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </Card>
  );
};
const renderAgentDetailModal = () => {
  if (!selectedAgent) return null;

  const agent = selectedAgent;
  const perf = agent.performance || {};
  const info = agent.agentInfo || {};

  const totalLeads = perf.totalLeads || 0;
  const totalSales = perf.stageBreakdown?.['Sales Made'] || 0;
  const conversionRate = totalLeads ? ((totalSales / totalLeads) * 100).toFixed(1) : 0;

  const stageData = Object.entries(perf.stageBreakdown || {})
    .filter(([_, count]) => count > 0)
    .map(([stage, count]) => ({ stage, count, color: STAGE_COLORS[stage] }))
    .sort((a, b) => b.count - a.count);

  const domainData = Object.entries(perf.domainBreakdown || {})
    .filter(([_, count]) => count > 0)
    .map(([domain, count]) => ({ 
      domain: domain.replace('.com', '').replace('premiumused', 'PU').replace('advanceused', 'AU'), 
      fullDomain: domain,
      count 
    }))
    .sort((a, b) => b.count - a.count);

  const tierData = [
    { tier: 'TIER-1', count: perf.tierBreakdown?.['TIER-1'] || 0, color: COLORS.success },
    { tier: 'TIER-2', count: perf.tierBreakdown?.['TIER-2'] || 0, color: COLORS.warning },
    { tier: 'TIER-3', count: perf.tierBreakdown?.['TIER-3'] || 0, color: COLORS.error }
  ].filter(t => t.count > 0);

  return (
    <Modal
      open={agentDetailModal}
      onCancel={() => setAgentDetailModal(false)}
      width={1200}
      footer={null}
      style={{ top: 20 }}
      bodyStyle={{ padding: 0 }}
    >
      {/* Header Section with Gradient Background */}
      <div style={{ 
        background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.geekblue} 100%)`,
        padding: '32px 24px',
        borderRadius: '12px 12px 0 0',
        color: COLORS.white
      }}>
        <Row align="middle" gutter={[24, 16]}>
          <Col flex="none">
            <ProductionAgentAvatar name={info.name} isActive={info.isActive} size={80} />
          </Col>
          <Col flex="auto">
            <div>
              <Title level={3} style={{ color: COLORS.white, margin: 0 }}>{info.name || 'Unassigned'}</Title>
              <Space size={16} style={{ marginTop: 8 }}>
                <Tag icon={<UserOutlined />} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: COLORS.white }}>
                  {info.role || 'Agent'}
                </Tag>
                <Tag icon={<MailOutlined />} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: COLORS.white }}>
                  {info.email || 'No email'}
                </Tag>
                <Tag icon={<PhoneOutlined />} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: COLORS.white }}>
                  {info.directNumber || info.extension || 'No phone'}
                </Tag>
              </Space>
            </div>
          </Col>
          <Col flex="none">
            <ProductionStatusBadge status={info.isActive ? 'Active' : 'Inactive'} />
          </Col>
        </Row>
      </div>

      {/* Content Section */}
      <div style={{ padding: '24px' }}>
        {/* Key Metrics Row */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={8} md={6}>
            <div style={{ 
              textAlign: 'center', 
              padding: '16px', 
              background: `${COLORS.primary}08`, 
              borderRadius: 12,
              border: `1px solid ${COLORS.border}`
            }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Total Leads</Text>
              <div style={{ fontSize: 28, fontWeight: 700, color: COLORS.primary }}>{formatNumber(totalLeads)}</div>
            </div>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <div style={{ 
              textAlign: 'center', 
              padding: '16px', 
              background: `${COLORS.success}08`, 
              borderRadius: 12,
              border: `1px solid ${COLORS.border}`
            }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Sales</Text>
              <div style={{ fontSize: 28, fontWeight: 700, color: COLORS.success }}>{formatNumber(totalSales)}</div>
            </div>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <div style={{ 
              textAlign: 'center', 
              padding: '16px', 
              background: `${COLORS.warning}08`, 
              borderRadius: 12,
              border: `1px solid ${COLORS.border}`
            }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Conversion Rate</Text>
              <div style={{ fontSize: 28, fontWeight: 700, color: COLORS.warning }}>{conversionRate}%</div>
            </div>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <div style={{ 
              textAlign: 'center', 
              padding: '16px', 
              background: `${COLORS.purple}08`, 
              borderRadius: 12,
              border: `1px solid ${COLORS.border}`
            }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Quotes</Text>
              <div style={{ fontSize: 28, fontWeight: 700, color: COLORS.purple }}>{formatNumber(perf.stageBreakdown?.['Quoted'] || 0)}</div>
            </div>
          </Col>
        </Row>

        <Tabs defaultActiveKey="overview" size="large" type="card">
          {/* Overview Tab */}
          <TabPane tab={<span><DashboardOutlined /> Overview</span>} key="overview">
            <Row gutter={[24, 24]} style={{ marginTop: 16 }}>
              {/* Lead Types & Tiers */}
              <Col xs={24} md={12}>
                <Card title="Lead Distribution" size="small" style={{ borderRadius: 12 }}>
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ 
                          width: 80, 
                          height: 80, 
                          margin: '0 auto',
                          background: `${COLORS.primary}15`,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <RiseOutlined style={{ fontSize: 32, color: COLORS.primary }} />
                        </div>
                        <div style={{ marginTop: 12 }}>
                          <Text type="secondary">Inbound</Text>
                          <div style={{ fontSize: 24, fontWeight: 700, color: COLORS.primary }}>
                            {formatNumber(perf.leadTypeBreakdown?.inbound || 0)}
                          </div>
                        </div>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ 
                          width: 80, 
                          height: 80, 
                          margin: '0 auto',
                          background: `${COLORS.orange}15`,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <FallOutlined style={{ fontSize: 32, color: COLORS.orange }} />
                        </div>
                        <div style={{ marginTop: 12 }}>
                          <Text type="secondary">Outbound</Text>
                          <div style={{ fontSize: 24, fontWeight: 700, color: COLORS.orange }}>
                            {formatNumber(perf.leadTypeBreakdown?.outbound || 0)}
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card>
              </Col>

              {/* Tier Distribution */}
              <Col xs={24} md={12}>
                <Card title="Tier Performance" size="small" style={{ borderRadius: 12 }}>
                  {tierData.length > 0 ? (
                    <div>
                      {tierData.map((tier) => (
                        <div key={tier.tier} style={{ marginBottom: 16 }}>
                          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                            <Tag color={tier.tier === 'TIER-1' ? 'success' : tier.tier === 'TIER-2' ? 'warning' : 'error'}>
                              {tier.tier}
                            </Tag>
                            <Text strong style={{ fontSize: 18, color: tier.color }}>{formatNumber(tier.count)}</Text>
                          </Space>
                          <Progress 
                            percent={(tier.count / totalLeads) * 100} 
                            strokeColor={tier.color}
                            showInfo={false}
                            size="small"
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Empty description="No tier data available" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  )}
                </Card>
              </Col>

            <Col span={24}>
  <Card title="Contact Information" size="small" style={{ borderRadius: 12 }}>
    <Descriptions column={{ xs: 1, sm: 2, md: 2 }} size="small">
      <Descriptions.Item label="Username">
        <Space><UserOutlined /> {info.userName || 'N/A'}</Space>
      </Descriptions.Item>
      <Descriptions.Item label="Full Name">
        <Space><UserOutlined /> {info.name || 'N/A'}</Space>
      </Descriptions.Item>
      <Descriptions.Item label="Email">
        <Space><MailOutlined /> {info.email || 'N/A'}</Space>
      </Descriptions.Item>
      <Descriptions.Item label="Phone">
        <Space><PhoneOutlined /> {info.directNumber || info.extension || 'N/A'}</Space>
      </Descriptions.Item>
      <Descriptions.Item label="Role">
        <Space><UserOutlined /> {info.role || 'N/A'}</Space>
      </Descriptions.Item>
      <Descriptions.Item label="Extension">
        <Space><PhoneOutlined /> {info.extension || 'N/A'}</Space>
      </Descriptions.Item>
      <Descriptions.Item label="Direct Number">
        <Space><PhoneOutlined /> {info.directNumber || 'N/A'}</Space>
      </Descriptions.Item>
      <Descriptions.Item label="Last Activity">
        <Space><ClockCircleOutlined /> {perf.lastActivity ? dayjs(perf.lastActivity).format('MMM DD, YYYY HH:mm') : 'N/A'}</Space>
      </Descriptions.Item>
    </Descriptions>
  </Card>
</Col>
            </Row>
          </TabPane>

          {/* Stage Breakdown Tab */}
          <TabPane tab={<span><PieChartOutlined /> Stages</span>} key="stages">
            <div style={{ marginTop: 16 }}>
              {stageData.length > 0 ? (
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={10}>
                    <div style={{ height: 300 }}>
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie
                            data={stageData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={100}
                            dataKey="count"
                            label={({ stage, percent }) => `${stage}: ${(percent * 100).toFixed(0)}%`}
                          >
                            {stageData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </Col>
                  <Col xs={24} md={14}>
                    <List
                      dataSource={stageData}
                      renderItem={(stage) => (
                        <List.Item>
                          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                            <Space>
                              <div style={{ width: 12, height: 12, backgroundColor: stage.color, borderRadius: 2 }} />
                              <Text strong>{stage.stage}</Text>
                            </Space>
                            <Space>
                              <Badge count={stage.count} style={{ backgroundColor: stage.color }} />
                              <Progress 
                                percent={(stage.count / totalLeads) * 100} 
                                size="small" 
                                style={{ width: 100 }}
                                strokeColor={stage.color}
                                showInfo={false}
                              />
                              <Text type="secondary">{((stage.count / totalLeads) * 100).toFixed(1)}%</Text>
                            </Space>
                          </Space>
                        </List.Item>
                      )}
                    />
                  </Col>
                </Row>
              ) : (
                <Empty description="No stage data available" />
              )}
            </div>
          </TabPane>

          {/* Domain Breakdown Tab */}
          <TabPane tab={<span><GlobalOutlined /> Domains</span>} key="domains">
            <div style={{ marginTop: 16 }}>
              {domainData.length > 0 ? (
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={8}>
                    <div style={{ height: 300 }}>
                      <ResponsiveContainer>
                        <BarChart
                          data={domainData.slice(0, 8)}
                          layout="vertical"
                          margin={{ left: 60, right: 20, top: 20, bottom: 20 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis type="category" dataKey="domain" width={80} tick={{ fontSize: 10 }} />
                          <RechartsTooltip />
                          <Bar dataKey="count" fill={COLORS.primary} radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </Col>
                  <Col xs={24} md={16}>
                    <Table
                      dataSource={domainData}
                      columns={[
                        { 
                          title: 'Domain', 
                          dataIndex: 'domain', 
                          key: 'domain',
                          render: (text, record) => (
                            <Tooltip title={record.fullDomain}>
                              <Tag color="geekblue">{text}</Tag>
                            </Tooltip>
                          )
                        },
                        { 
                          title: 'Leads', 
                          dataIndex: 'count', 
                          key: 'count', 
                          align: 'right',
                          sorter: (a, b) => a.count - b.count,
                          render: (val) => <Text strong>{formatNumber(val)}</Text>
                        },
                        { 
                          title: 'Percentage', 
                          key: 'percentage', 
                          align: 'right',
                          render: (_, record) => (
                            <Space>
                              <Progress 
                                percent={(record.count / totalLeads) * 100} 
                                size="small" 
                                style={{ width: 80 }}
                                showInfo={false}
                              />
                              <Text>{((record.count / totalLeads) * 100).toFixed(1)}%</Text>
                            </Space>
                          )
                        }
                      ]}
                      rowKey="domain"
                      pagination={{ pageSize: 5, showSizeChanger: true }}
                      size="small"
                    />
                  </Col>
                </Row>
              ) : (
                <Empty description="No domain data available" />
              )}
            </div>
          </TabPane>
        </Tabs>
      </div>
    </Modal>
  );
};

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Spin size="large" tip="Loading production dashboard..." />
      </div>
    );
  }

  return (
    <div ref={printRef} style={{ background: COLORS.background, minHeight: '100vh' }}>
      {renderHeader()}
      
      <div style={{ padding: isMobile ? '0 16px' : '0 24px' }}>
        {renderProductionFilterBar()}
                {renderProductionStats()}

        {/* Agents Section with View Toggle */}
        <Card 
          title={
            <Space>
              <TeamOutlined />
              <span>Agent Roster</span>
              <Badge count={processedData?.agentComparisonData?.length || 0} style={{ backgroundColor: COLORS.primary }} />
            </Space>
          }
          extra={
            <Space>
              <Button 
                type={viewMode === 'grid' ? 'primary' : 'default'}
                icon={<AppstoreOutlined />}
                onClick={() => setViewMode('grid')}
                size="small"
              />
              <Button 
                type={viewMode === 'table' ? 'primary' : 'default'}
                icon={<TableOutlined />}
                onClick={() => setViewMode('table')}
                size="small"
              />
            </Space>
          }
          style={{ borderRadius: '12px', marginBottom: '24px' }}
        >
          {viewMode === 'grid' ? renderAgentsGridView() : renderAgentsTableView()}
        </Card>

        {/* Full Leaderboard - All Agents in Order */}
        {renderProductionLeaderboard()}
        
        {/* Charts Section */}
        {renderStageDistribution()}
        {renderDomainPerformance()}
        {renderPerformanceChart()}
      </div>

      {renderAgentDetailModal()}
    </div>
  );

  // Helper function for filter bar
  function renderProductionFilterBar() {
    return (
    <ProductionFilterBar
    filters={filters}
    onFilterChange={handleFilterChange}
    onSearch={handleSearch}
    onClear={handleClearFilters}
    stages={uniqueStages}
    dateRange={dateRange}
    onDateRangeChange={handleDateRangeChange}
    onRefresh={handleRefresh}
    refreshing={refreshing}
    onExport={handleExport}
    exportLoading={exportLoading}
    onPrint={handlePrint}
  />
    );
  }
};

export default AgentPerformanceDashboard;


